import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import NumbersIcon from "@mui/icons-material/Numbers";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import BusinessIcon from "@mui/icons-material/Business";
import { ImportedDocument } from "../importedDocumentsApi";
import { StructureName } from "../../../device/Structure";
import { Localized } from "@fluent/react";
import { ImportedDocumentStatusChip } from "./ImportedDocumentStatusChip";

type Props = {
    document: ImportedDocument;
};

export const ImportedDocumentHeader: React.FC<Props> = ({ document }) => {
    return (
        <Paper variant="outlined" sx={{ p: 0, mb: 1.5, overflow: "hidden" }}>
            <Box
                sx={{
                    bgcolor: (t) => (t.palette.mode === "dark" ? "grey.900" : "grey.100"),
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    px: 1.25,
                    py: 1.25,
                }}
            >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                    <Stack alignItems="center" spacing={0.25} sx={{ width: 80, flexShrink: 0 }}>
                        <DescriptionIcon color="primary" sx={{ fontSize: 40 }} />
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 700, textAlign: "center", lineHeight: 1.2 }}
                            title={document.type ?? ""}
                        >
                            {document.type ?? "—"}
                        </Typography>
                    </Stack>

                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: ".02em" }}>
                            <Localized id="title">
                                <span>_Tytuł</span>
                            </Localized>
                        </Typography>
                        <Box
                            sx={{
                                mt: 0.25,
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                bgcolor: "background.paper",
                                border: "1px solid",
                                borderColor: "divider",
                                fontWeight: 800,
                                minWidth: 0,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                            title={document.title ?? ""}
                        >
                            {document.title ?? "—"}
                        </Box>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                        <ImportedDocumentStatusChip status={document.status} />
                    </Stack>
                </Stack>
            </Box>

            <Box sx={{ px: 1.25, py: 1.25 }}>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={3}
                    alignItems="center"
                    sx={{ minWidth: 0 }}
                >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <NumbersIcon fontSize="small" />
                        <Typography variant="body2" noWrap>
                            <strong>
                                <Localized id="number">
                                    <span>_Numer</span>
                                </Localized>
                                :
                            </strong>
                            {`${document.number ?? "—"} / v. ${document.version ?? "—"}`}
                        </Typography>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <AccountTreeIcon fontSize="small" />
                        <Typography variant="body2" noWrap>
                            <strong>
                                <Localized id="structure">
                                    <span>_Struktura</span>
                                </Localized>
                                :
                            </strong>&nbsp;
                            {document.structureId ? (
                                <StructureName structureId={String(document.structureId)} />
                            ) : (
                                "—"
                            )}
                        </Typography>

                    </Stack>

                    {/* Wystawca */}
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <BusinessIcon fontSize="small" />
                        <Typography variant="body2" noWrap title={document.counterparty?.displayName ?? document.relatedCompanyName ?? ""}>
                            <strong>
                                <Localized id="document-document-company-owner">
                                    <span>_Wystawca</span>
                                </Localized>
                                :
                            </strong>&nbsp;
                            {document.counterparty?.displayName ?? "—"}
                            {document.counterparty?.countryCode ? ` (${document.counterparty.countryCode})` : ""}
                        </Typography>
                    </Stack>
                </Stack>
            </Box>

        </Paper>
    );
};
